import { useEffect, useState } from 'react';

import { BudgetHoverOverlay } from '@/features/charts/components/chart-budget-overlay';
import { BasicGradientWarningIcon } from '@/features/charts/components/chart-warning-icon';
import { useBudgetOverlay } from '@/features/charts/hooks/use-budget-overlay';
import { getCustomOverlayPosition } from '@/features/charts/util/chart-styling-util';
import {
  ChartResult,
  ChartSettings,
  CustomToolTipData,
} from '@/features/charts/util/chart-util';
import { useKpiTranslations } from '@/features/projects/project-details/components/kpi-card';
import { Gradients } from '@/shared/components/theme/gradient';
import { ChartTooltipContentSingle } from '@/shared/components/ui/custom-chart-tooltip';
import { getKpiUnitAbbreviationTranslation } from '@/shared/hooks/use-kpi-unit-translation';
import { cn } from '@/shared/utils/cn';

const BatteryBarsLength = 10;

export const CustomBatteryChart = ({
  chartResult,
  chartSettings,
  maxValue,
  className,
}: {
  chartResult: ChartResult;
  chartSettings?: ChartSettings;
  maxValue: number;
  className?: string;
}) => {
  const { chartData, chartConfig } = chartResult;
  const kpiCount = Object.keys(chartConfig).length;

  if (kpiCount > 1) {
    throw new Error('Battery chart only supports one kpi');
  }

  const currentValue = Number(chartData[0]?.value || 0);
  const isOverMax = currentValue > maxValue;
  const exactBars = isOverMax
    ? BatteryBarsLength
    : (currentValue / maxValue) * BatteryBarsLength;
  const filledBars = Math.floor(exactBars);
  const hasHalfBar = !isOverMax && exactBars % 1 >= 0.5;

  const [animatedBars, setAnimatedBars] = useState(0);
  const [showHalfBar, setShowHalfBar] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [hoveredBarIndex, setHoveredBarIndex] = useState<number | null>(null);
  const [tooltipPosition, setTooltipPosition] = useState({ x: 0, y: 0 });

  const {
    isBudgetHovered,
    mousePosition,
    handleBudgetMouseEnter,
    handleBudgetMouseLeave,
  } = useBudgetOverlay();

  const unit = chartResult.chartData[0]?.unit;
  const { tKpiTypes } = useKpiTranslations();
  const unitAbbreviation = getKpiUnitAbbreviationTranslation(
    tKpiTypes,
    unit || '',
  );

  useEffect(() => {
    const interval = setInterval(() => {
      setAnimatedBars((prev) => {
        if (prev < filledBars) {
          return prev + 1;
        }
        if (prev === filledBars && hasHalfBar) {
          setShowHalfBar(true);
        }
        clearInterval(interval);
        return prev;
      });
    }, 50);

    return () => clearInterval(interval);
  }, [filledBars, hasHalfBar]);

  const getBarState = (index: number) => {
    if (isOverMax) {
      return index < animatedBars ? 'overMax' : 'empty';
    }
    if (index < animatedBars) {
      return 'filled';
    }
    if (index === animatedBars && showHalfBar) {
      return 'half';
    }
    return 'empty';
  };

  const handleBarMouseEnter = (event: React.MouseEvent, barIndex: number) => {
    const barState = getBarState(barIndex);
    const isEmptyBar = barState === 'empty';

    if (isEmptyBar) {
      handleBudgetMouseEnter();
    } else {
      setIsHovered(true);
      setHoveredBarIndex(barIndex);
      setTooltipPosition(
        getCustomOverlayPosition(event.clientX, event.clientY),
      );
    }
  };

  const handleBarMouseMove = (event: React.MouseEvent) => {
    if (isHovered) {
      setTooltipPosition(
        getCustomOverlayPosition(event.clientX, event.clientY),
      );
    }
  };

  const handleBarMouseLeave = () => {
    setIsHovered(false);
    setHoveredBarIndex(null);
    handleBudgetMouseLeave();
  };

  const getTooltipData = () => {
    if (hoveredBarIndex === null) return null;

    const barState = getBarState(hoveredBarIndex);
    const isFilledBar =
      barState === 'filled' || barState === 'overMax' || barState === 'half';

    if (isFilledBar) {
      return {
        payload: [
          {
            payload: {
              budget: maxValue,
              unit: chartResult.chartData[0]?.unit,
              date: chartResult.chartData[0]?.date,
              lastEntryValue: chartResult.chartData[0]?.lastEntryValue,
            },
            value: currentValue,
          },
        ],
      } as CustomToolTipData;
    } else {
      const remainingValue = maxValue - currentValue;
      return {
        payload: [
          {
            payload: {
              value: remainingValue,
              budget: maxValue,
              unit: chartResult.chartData[0]?.unit,
            },
            value: remainingValue,
          },
        ],
      } as CustomToolTipData;
    }
  };

  const tooltipData = getTooltipData();

  return (
    <div className={cn('relative h-full', className)}>
      <div className="flex h-full flex-row items-center justify-center gap-2">
        {Array.from({ length: BatteryBarsLength }).map((_, index) => {
          const barState = getBarState(index);

          return (
            <div
              key={index}
              className={cn(
                'relative h-[80%] w-full max-w-32 overflow-hidden rounded-full',
                barState === 'filled'
                  ? `${Gradients.purpleBlueGradient} bg-gradient-to-b`
                  : barState === 'overMax'
                    ? `${Gradients.pinkRedGradient} bg-gradient-to-br`
                    : 'bg-[rgba(242,242,242,0.01)] shadow-[0_7.729px_45.214px_0_rgba(255,255,255,0.15)_inset]',
                chartSettings?.height,
              )}
              onMouseEnter={(e) => handleBarMouseEnter(e, index)}
              onMouseMove={handleBarMouseMove}
              onMouseLeave={handleBarMouseLeave}
            >
              {barState === 'half' && (
                <div
                  className={`absolute inset-0 ${Gradients.purpleBlueGradient} w-[50%] bg-gradient-to-b`}
                ></div>
              )}
            </div>
          );
        })}
        {isOverMax && (
          <div className="relative ml-[-10px]">
            <BasicGradientWarningIcon
              maxValue={maxValue}
              currValue={currentValue}
              unit={unitAbbreviation}
            />
          </div>
        )}
      </div>

      {/* Tooltip */}
      {isHovered && tooltipData && (
        <div
          className="pointer-events-none fixed z-50"
          style={{
            left: tooltipPosition.x,
            top: tooltipPosition.y,
          }}
        >
          <ChartTooltipContentSingle {...tooltipData} />
        </div>
      )}

      {/* Budget Overlay */}
      {isBudgetHovered && (
        <BudgetHoverOverlay
          isVisible={isBudgetHovered}
          mouseX={mousePosition.x}
          mouseY={mousePosition.y}
          maxValue={maxValue}
          currValue={currentValue}
          unit={unitAbbreviation}
        />
      )}
    </div>
  );
};
